'use client';

import { useState, type ReactElement } from 'react';
import { getProductContentLayout } from './product-content-layout';
import styles from './product-tabs.module.css';
import { contentFieldKey } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { ProductContentField } from './product-content-field';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { ProductTabDescription } from './product-tab-description';
import { ProductTabParameters } from './product-tab-parameters';
import { ProductTabReviews } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-reviews';
import { AgentCard } from '../agent-card';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { ProductTabVideo } from './product-tab-video';
import { ProductTabAttachments } from './product-tab-attachments';
import { ProductTabContainer } from './product-tab-container';

interface Tab {
  id: number | string;
  name: string;
  content: ReactElement;
  show: boolean;
  keepMounted?: boolean;
}

interface ProductTabsProps {
  product: ProductDetailData;
  locale: string;
}

export function ProductTabs({ product, locale }: ProductTabsProps) {
  const firstTab =
    product.description || product.short_description ? '1' : product.attributes?.length ? '2' : '3';
  const [selection, setSelection] = useState({ slug: product.slug, tab: firstTab });
  const requestedTab = selection.slug === product.slug ? selection.tab : firstTab;
  const additionalTabs = getProductContentLayout(product).contentTabs;
  const availableIds = new Set([
    ...(product.description || product.short_description ? ['1'] : []),
    ...(product.attributes?.length ? ['2'] : []),
    '3',
    ...(product.video ? ['4'] : []),
    ...(product.attachments?.length ? ['5'] : []),
    ...additionalTabs.map(tab => tab.key),
  ]);
  const activeTab = availableIds.has(requestedTab) ? requestedTab : firstTab;

  const me = useCustomerStore(s => s.me);
  const { t } = useTranslations();

  const { data: reviews, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug, authToken: me?.authToken }),
    enabled: activeTab === '3',
  });

  const reviewCount = reviews?.data.length ?? product.ratingCount ?? product.reviews_count ?? 0;

  const tabs: Tab[] = [
    {
      id: 1,
      name: t('text231'),
      content: (
        <ProductTabDescription
          product={{ ...product, description: product.description || product.short_description || '' }}
          title={t('text231')}
        />
      ),
      show: !!(product?.description || product?.short_description),
      keepMounted: true,
    },
    {
      id: 2,
      name: t('text9'),
      content: <ProductTabParameters product={product} title={t('text9')} />,
      show: Array.isArray(product?.attributes) && product.attributes.length > 0,
    },
    {
      id: 3,
      name: `${t('text587')} (${reviewCount})`,
      content: (
        <ProductTabReviews
          product={product}
          reviews={reviews?.data ?? []}
          title={t('text587')}
          isLoading={isLoading}
        />
      ),
      show: true,
    },
    {
      id: 4,
      name: t('text451'),
      content: <ProductTabVideo product={product} title={t('text451')} />,
      show: !!product.video,
    },
    {
      id: 5,
      name: `${t('text333')} (${product.attachments?.length})`,
      content: <ProductTabAttachments attachments={product.attachments!} title={t('text333')} />,
      show: !!product.attachments?.length,
    },
    ...additionalTabs.map(tab => ({
      id: tab.key,
      name: tab.name,
      content: (
        <ProductTabContainer title={tab.name}>
          {tab.fields.map(field => (
            <ProductContentField
              key={contentFieldKey('products', field.id)}
              field={field}
            />
          ))}
        </ProductTabContainer>
      ),
      show: true,
      keepMounted: true,
    })),
  ];

  return (
    <div className="custom-section">
      <div className="custom-container flex min-w-0 flex-col items-start justify-start gap-8 sm:gap-10 lg:gap-12">
        <Tabs
          value={activeTab}
          onValueChange={tab => setSelection({ slug: product.slug, tab })}
          className="flex w-full min-w-0 flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12"
        >
          <TabsList
            className={cn(
              'flex h-fit w-full items-center justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 pb-3',
              'ea-product-tabs__tabs-list',
              styles.tabList,
            )}
          >
            {tabs.map(tab => {
              if (!tab.show) return null;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id.toString()}
                  className={cn(
                    'flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-[50px] px-3 text-sm transition-all',
                    'ea-universal-tabs__tab-trigger',
                  )}
                >
                  {tab.name}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="grid w-full min-w-0 grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-12">
            <div className="min-w-0">
              {tabs
                .filter(tab => tab.show)
                .map(tab => (
                  <TabsContent
                    key={tab.id}
                    value={tab.id.toString()}
                    forceMount={tab.keepMounted ? true : undefined}
                    hidden={tab.id.toString() !== activeTab}
                    className="mt-0 w-full data-[state=inactive]:hidden"
                  >
                    {tab.content}
                  </TabsContent>
                ))}
            </div>
            <AgentCard />
          </div>
        </Tabs>
      </div>
    </div>
  );
}
